import { getLocale, msg } from "@omegagrid/localize";
import { MenuItem, dates } from "@omegagrid/core";
import { NodeModel, TreeOptions, TreeSource } from "@omegagrid/tree";
import { BrowserAdapter } from "@reporting/rp-app-host";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { ModuleDefinition } from "@reporting/rp-types/definition";
import { HistoryReportContext } from "@reporting/rp-types/analytics";
import { html, render, css } from "lit";
import rp from "@reporting/rp-app-host";
import { Sidebar } from "../components";
import { getContextIcon } from "./contextBrowserAdapter";

export class ContextHistoryBrowserAdapter extends BrowserAdapter {

	constructor(
		private modules: Map<ModuleCode, ModuleDefinition>,
	) { super() }

	_dateFormatter: dates.DateFormatterFunction = null;
	get dateFormatter() {
		if (!this._dateFormatter) {
			this._dateFormatter = dates.getDateFormatter(getLocale(), dates.getDateFormat({withTime: false}));
		}
		return this._dateFormatter;
	}

	async getTree(): Promise<TreeSource> {
		const items = await rp.ms('analytics').get<HistoryReportContext[]>(`history`, {
			fields: ['id_context', 'name', 'module', 'context_view', 'date_created']
		});

		const treeItems: TreeSource = [];
		let lastDate: string = null;
		items.forEach(item => {
			const date = item.date_created?.substring(0, 10);

			if (date != lastDate) {
				treeItems.push({
					k: `date-${date}`,
					v: this.dateFormatter(item.date_created),
					l: 0,
				});
			}

			treeItems.push({
				k: item.id_context.toString(),
				v: item.id_context.toString(),
				l: 1,
				props: {context: item}
			});

			lastDate = date;
		});

		return treeItems;
	}

	async getMenuItems(node: NodeModel): Promise<MenuItem[]> {
		const context = node.properties?.context as HistoryReportContext;
		if (!context) return [];
		return [
			...(context.name ? [
				{key: 'properties', value: msg('Properties'), icon: 'pen-to-square'}
			] : []),
			{key: 'filters', value: msg('Filters'), icon: 'filter'},
			{key: 'contextData', value: msg('Context JSON'), icon: 'brackets-curly'}
		];
	}

	async onAction(key: string, node: NodeModel) {
		const context = node.properties.context as HistoryReportContext;
		switch (key) {
			case 'properties':
				rp.host.openModalSidebar({
					id: `analytics-contextForm-${context.id_context}`,
					caption: `#${context.id_context} ${context.name}`
				});
				break;
			case 'filters':
				const sidebar = await rp.host.components.get('analytics') as Sidebar;
				sidebar.updateState({activeTab: 'filters', components: {filters: {
					idContext: context.id_context,
					module: context.module as ModuleCode
				}}}, true);
				break;
			case 'contextData':
				rp.host.open(`analytics-contextDataEditor-${context.id_context}`);
				break;
		}
	}

	_nodeRenderer = (div: HTMLDivElement, node: NodeModel) => {
		if (!node.properties) {
			div.textContent = node.value as string;
			return;
		}

		const context = node.properties.context as HistoryReportContext;
		const time = dates.format(context.date_created, 'HH:mm:ss');

		render(html`
			<div class="node-content">
				<og-icon .icon="${getContextIcon(context, this.modules.get(context.module as ModuleCode))}">
				</og-icon>
				<span>${time}</span>
				<span>&nbsp|&nbsp</span>
				<span>#${context.id_context}</span>
				<span>&nbsp|&nbsp</span>
				<span class="name">
					${context.name ? context.name : html`${`<${msg('not-saved')}>`}`}
				</span>
			</div>
		`, div);
	}

	getOptions(): Partial<TreeOptions> {
		return this.createOptions({
			nodeRenderer: this._nodeRenderer,
			selectMode: 'none',
			expanders: false,
			contextMenuButton: true,
			customStyle: css`
				.node-content {
					display: flex;
					flex-direction: row;
					align-items: center;
				}
			`,
			folderOptions: {
				openIcon: {icon: ['fal', 'folder-open'], statusIcon: ['fas', 'calendar']},
				closedIcon: {icon: ['fal', 'folder'], statusIcon: ['fas', 'calendar']},
			}
		});
	}
}
